import { z } from 'zod';
import { tierSchema, appointmentStatusSchema, appointmentLabelSchema, emrSyncStatusSchema } from '@forcebase-ai/core';

// src/patient.ts
var patientMeSchema = z.object({
  id: z.string().uuid(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  dob: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  insurance_member_ref: z.string().nullable().optional(),
  insurance_carrier: z.string().nullable().optional(),
  tier: tierSchema,
  status: z.string(),
  healthie_patient_id: z.string().nullable().optional(),
  avatar_url: z.string().nullable().optional()
});
var avatarUploadBodySchema = z.object({
  image: z.string().regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/, "must be a png/jpeg/webp data: URL").max(4e5, "image too large \u2014 resize client-side before upload")
});
var avatarResultSchema = z.object({ avatar_url: z.string() });
var chartLinkSchema = z.object({
  url: z.string(),
  expiresAt: z.string().nullable().optional()
});
var appointmentViewSchema = z.object({
  id: z.string().uuid(),
  patient_id: z.string().uuid(),
  doctor_id: z.string().uuid(),
  scheduled_at: z.string(),
  duration_minutes: z.number(),
  label: appointmentLabelSchema,
  status: appointmentStatusSchema,
  zoom_join_url: z.string().nullable().optional(),
  zoom_passcode: z.string().nullable().optional(),
  healthie_appointment_id: z.string().nullable().optional(),
  emr_sync_status: emrSyncStatusSchema.nullable().optional(),
  cancelled_at: z.string().nullable().optional(),
  cancel_reason: z.string().nullable().optional(),
  created_at: z.string()
});
var appointmentEventSchema = z.object({
  id: z.string().uuid(),
  event: z.enum(["booked", "rescheduled", "cancelled", "status_changed", "no_show"]),
  actor_role: z.string().nullable().optional(),
  from_time: z.string().nullable().optional(),
  to_time: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  created_at: z.string()
});
var doctorSummarySchema = z.object({
  id: z.string().uuid(),
  first_name: z.string(),
  last_name: z.string(),
  specialty: z.string().nullable().optional()
});
var availabilitySchema = z.object({
  doctor_id: z.string().uuid(),
  slots: z.array(z.string())
});
var schedulingConfigSchema = z.object({
  reschedule_min_lead_minutes: z.number(),
  cancel_min_lead_minutes: z.number(),
  late_cancel_policy: z.string(),
  visit_reminder_offsets: z.array(z.string())
});
var bookRequestSchema = z.object({
  doctor_id: z.string().uuid(),
  scheduled_at: z.string(),
  label: appointmentLabelSchema.optional()
});
var rescheduleBodySchema = z.object({
  new_scheduled_at: z.string(),
  reason: z.string().min(1)
});
var cancelBodySchema = z.object({ reason: z.string().min(1) });
var joinTicketSchema = z.object({
  meetingNumber: z.string(),
  joinUrl: z.string().nullable().optional(),
  passcode: z.string().nullable().optional(),
  signature: z.string(),
  sdkKey: z.string().nullable().optional(),
  role: z.literal(0)
});
var chatMessageSchema = z.object({
  id: z.string().uuid(),
  sender_role: z.string(),
  body: z.string(),
  created_at: z.string()
});
var chatThreadSchema = z.object({
  thread_id: z.string().uuid(),
  messages: z.array(chatMessageSchema)
});
var visitSummaryViewSchema = z.object({
  id: z.string().uuid(),
  last_visit_at: z.string(),
  summary_text: z.string(),
  healthie_appointment_id: z.string().nullable().optional()
});
var billingSummarySchema = z.object({
  tier: tierSchema.nullable(),
  status: z.string().nullable(),
  subscription: z.object({
    product: z.string(),
    status: z.string(),
    current_period_end: z.string().nullable().optional()
  }).nullable(),
  payments: z.array(
    z.object({
      amount_cents: z.number(),
      currency: z.string(),
      status: z.string(),
      created_at: z.string()
    })
  ),
  // Card-on-file display summary (brand + last4 only — never a PAN; D9). Optional so older API
  // builds that don't send it still validate; null when no card is on file.
  payment_method: z.object({
    brand: z.string(),
    last4: z.string(),
    exp_month: z.number().nullable().optional(),
    exp_year: z.number().nullable().optional()
  }).nullable().optional()
});
var notificationPrefsSchema = z.object({
  email: z.boolean(),
  push: z.boolean(),
  quiet_hours_start: z.string().nullable().optional(),
  quiet_hours_end: z.string().nullable().optional()
});
var walletPassResultSchema = z.object({
  provider: z.enum(["apple", "google"]),
  serial: z.string(),
  url: z.string()
});
var insuranceCardSchema = z.object({
  member_name: z.string(),
  member_ref: z.string().nullable().optional(),
  carrier: z.string().nullable().optional(),
  tier: tierSchema,
  plan_label: z.string()
});

export { appointmentEventSchema, appointmentViewSchema, availabilitySchema, avatarResultSchema, avatarUploadBodySchema, billingSummarySchema, bookRequestSchema, cancelBodySchema, chartLinkSchema, chatMessageSchema, chatThreadSchema, doctorSummarySchema, insuranceCardSchema, joinTicketSchema, notificationPrefsSchema, patientMeSchema, rescheduleBodySchema, schedulingConfigSchema, visitSummaryViewSchema, walletPassResultSchema };
//# sourceMappingURL=chunk-6NPRQHU4.js.map
//# sourceMappingURL=chunk-6NPRQHU4.js.map